import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import { switchMap, from, catchError, of, throwError, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NotificationApiService {
  protected http = inject(HttpClient);
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  constructor() {}

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.userNotificationList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.notification.clear().then(() =>
                appDb.notification.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    read: item.read ? 1 : 0,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.notification.bulkPut(
              data.items.map((item) => ({
                ...item,
                read: item.read ? 1 : 0,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  readAll(data) {
    return this.http
      .put(generateURL(apiPaths.userNotificationReadAll), {})
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'userNotificationKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('userNotificationKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  read(id) {
    return this.http.put(generateURL(apiPaths.userNotificationRead), [id]).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'userNotificationKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('userNotificationKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }

  delete(id) {
    return this.http
      .delete(generateURL(apiPaths.userNotificationId, { id }))
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'userNotificationKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('userNotificationKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  addDevice(fireBaseToken) {
    return this.http.post(generateURL(apiPaths.userNotificationAddDevice, {}), {
      fireBaseToken,
    });
  }
}
